import type { CurvaDocument } from "@/document/model/document";
import { getSceneNodeChildren } from "@/document/model/document";
import type { BooleanNode, SceneNode } from "@/document/model/nodes";
import { boundsFromContours, contoursToPathData, sceneNodeToContours, transformContours, transformPathData } from "./pathGeometry";
import { resolveBooleanGeometry, type BooleanOperandContours } from "./booleanGeometry";
import { resolveBooleanGeometryWithAvailablePathOps } from "./pathBooleanGeometry";

export function refreshBooleanNodes(document: CurvaDocument): void {
  for (const page of document.pages) {
    for (const node of page.children) {
      refreshBooleanNodeTree(node);
    }
  }
}

export function refreshBooleanNodesForChangedNodes(document: CurvaDocument, changedNodeIds: Iterable<string>): void {
  const dirtyIds = new Set(changedNodeIds);
  if (dirtyIds.size === 0) {
    return;
  }

  for (const page of document.pages) {
    for (const node of page.children) {
      refreshBooleanNodeTreeIfDirty(node, dirtyIds);
    }
  }
}

export function refreshBooleanNodeTree(node: SceneNode): void {
  const children = getSceneNodeChildren(node);
  if (children) {
    for (const child of children) {
      refreshBooleanNodeTree(child);
    }
  }

  if (node.type === "boolean") {
    refreshBooleanNode(node);
  }
}

function refreshBooleanNodeTreeIfDirty(node: SceneNode, dirtyIds: Set<string>): boolean {
  const children = getSceneNodeChildren(node);
  const childChanged = children
    ? children.reduce((changed, child) => refreshBooleanNodeTreeIfDirty(child, dirtyIds) || changed, false)
    : false;
  const selfChanged = dirtyIds.has(node.id);
  const subtreeChanged = selfChanged || childChanged;

  if (node.type === "boolean" && subtreeChanged) {
    refreshBooleanNode(node);
    return true;
  }

  return subtreeChanged;
}

export function refreshBooleanNode(node: BooleanNode): void {
  const cacheByNodeId = new Map((node.sourceGeometry ?? []).map(cache => [cache.nodeId, cache]));
  const operands: BooleanOperandContours[] = node.sourceNodes.map(source => {
    const cached = cacheByNodeId.get(source.id);
    const compatibleCached = cached && cacheMatchesSource(cached, source) ? cached : undefined;
    const freshContours = source.visible ? sceneNodeToContours(source) : [];
    const contours = !source.visible
      ? []
      : freshContours.length > 0
        ? freshContours
        : compatibleCached
          ? transformContours(compatibleCached.contours, compatibleCached.sourceBounds ?? compatibleCached.bounds, source.bounds)
          : [];
    const path = !source.visible
      ? undefined
      : freshContours.length > 0
        ? contoursToPathData(freshContours)
        : compatibleCached?.path
        ? boundsEqual(compatibleCached.sourceBounds ?? compatibleCached.bounds, source.bounds)
          ? compatibleCached.path
          : transformPathData(compatibleCached.path, compatibleCached.sourceBounds ?? compatibleCached.bounds, source.bounds)
        : contours.length > 0
          ? contoursToPathData(contours)
          : undefined;
    return {
      nodeId: source.id,
      sourceBounds: { ...source.bounds },
      sourceKey: sourceCacheKey(source),
      path,
      contours,
    };
  });
  const usableOperands = operands.filter(operand => operand.contours.length > 0);
  node.sourceGeometry = usableOperands.map(operand => {
    return {
      nodeId: operand.nodeId,
      sourceBounds: operand.sourceBounds,
      sourceKey: operand.sourceKey,
      bounds: boundsFromContours(operand.contours),
      path: operand.path,
      contours: operand.contours.map(contour => contour.map(point => ({ ...point }))),
    };
  });

  if (usableOperands.length === 0) {
    node.resolved = { path: "", contours: [] };
    node.bounds = boundsFromContours([]);
    return;
  }

  const resolved = usableOperands.length === 1
    ? {
        contours: usableOperands[0]!.contours,
        path: usableOperands[0]!.path ?? contoursToPathData(usableOperands[0]!.contours),
        bounds: boundsFromContours(usableOperands[0]!.contours),
      }
    : usableOperands.every(operand => operand.path?.trim())
      ? resolveBooleanGeometryWithAvailablePathOps(node.operation, usableOperands)
      : resolveBooleanGeometry(node.operation, usableOperands);

  node.resolved = {
    path: resolved.path,
    contours: resolved.contours,
  };
  node.bounds = resolved.bounds;
}

export function transformBooleanNodeGeometry(
  node: BooleanNode,
  fromBounds: { x: number; y: number; width: number; height: number },
  toBounds: { x: number; y: number; width: number; height: number },
): void {
  node.sourceGeometry = node.sourceGeometry?.map(cache => {
    const source = node.sourceNodes.find(candidate => candidate.id === cache.nodeId);
    const nextSourceBounds = source ? { ...source.bounds } : transformBounds(cache.sourceBounds ?? cache.bounds, fromBounds, toBounds);
    const nextContours = transformContours(cache.contours, cache.sourceBounds ?? cache.bounds, nextSourceBounds);
    return {
      nodeId: cache.nodeId,
      sourceBounds: nextSourceBounds,
      sourceKey: cache.sourceKey,
      bounds: boundsFromContours(nextContours),
      path: cache.path ? transformPathData(cache.path, cache.sourceBounds ?? cache.bounds, nextSourceBounds) : undefined,
      contours: nextContours,
    };
  });
  node.resolved = {
    path: node.resolved.path ? transformPathData(node.resolved.path, fromBounds, toBounds) : "",
    contours: transformContours(node.resolved.contours, fromBounds, toBounds),
  };
  node.bounds = { ...toBounds };
}

export function sourceCacheKey(node: SceneNode): string | undefined {
  return node.type === "text" ? JSON.stringify(node.text) : undefined;
}

function cacheMatchesSource(cache: NonNullable<BooleanNode["sourceGeometry"]>[number], source: SceneNode): boolean {
  const key = sourceCacheKey(source);
  return key === undefined || cache.sourceKey === key;
}

function boundsEqual(left: { x: number; y: number; width: number; height: number }, right: { x: number; y: number; width: number; height: number }): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}

function transformBounds(
  bounds: { x: number; y: number; width: number; height: number },
  fromBounds: { x: number; y: number; width: number; height: number },
  toBounds: { x: number; y: number; width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return {
    x: toBounds.x + (bounds.x - fromBounds.x) * scaleX,
    y: toBounds.y + (bounds.y - fromBounds.y) * scaleY,
    width: bounds.width * scaleX,
    height: bounds.height * scaleY,
  };
}
